import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AuthStore } from '@features/auth';
import { IntentSignerService } from '@features/intent-signer';
import { CARD_TRANSPORT, MissingFundingIntentError } from '@entities/card';
import { WalletStore } from '@entities/wallet';
import type {
  CardFreshToken,
  CardFundingSigner,
  CardGasEstimateInput,
  CardGasRequirement,
  CardOperation,
} from '@entities/card';

@Injectable({ providedIn: 'root' })
export class CardHttpFreshToken implements CardFreshToken {
  private readonly auth = inject(AuthStore);

  freshToken(pin: string): Promise<string> {
    return this.auth.getFreshToken('pin', pin);
  }
}

@Injectable({ providedIn: 'root' })
export class CardFundingHttpSigner implements CardFundingSigner {
  private readonly freshTokens = inject(CardHttpFreshToken);
  private readonly intentSigner = inject(IntentSignerService);
  private readonly walletStore = inject(WalletStore);
  private readonly transport = inject(CARD_TRANSPORT);

  async sign(operation: CardOperation, pin: string): Promise<void> {
    if (!operation.fundingIntentId) throw new MissingFundingIntentError();
    const freshToken = await this.freshTokens.freshToken(pin);
    // The funding intent is prepared server-side, so `prepare()` never ran
    // here — the meta seeds the persisted resume record. The wallet/chain
    // placeholders get superseded by `GET /intents/:id` on resume.
    await this.intentSigner.signAndSubmit(operation.fundingIntentId, freshToken, {
      kind: 'card-funding',
      walletId: this.walletStore.activeWallet()?.id ?? '',
      chainId: '',
    });
  }

  async estimateGas(input: CardGasEstimateInput): Promise<CardGasRequirement | null> {
    try {
      const fee = await firstValueFrom(this.transport.fundingFee(input.walletId, input.amountRaw));
      if (fee.feeTokenAddress !== 'native') return null;
      return { requiredRaw: fee.networkFeeRaw, decimals: fee.feeTokenDecimals };
    } catch {
      return null;
    }
  }
}
